<template>
    <Head title="Change Subscription Plan" />

    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
            Change Subscription Plan
          </h1>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Upgrade or downgrade your subscription plan
          </p>
        </div>
        <Link :href="route('settings.billing.index')">
          <Button variant="secondary">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Billing
          </Button>
        </Link>
      </div>

      <!-- Current Plan -->
      <Card>
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            Current Plan
          </h2>
          <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            Active
          </span>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {{ currentSubscription.plan.name }}
            </h3>
            <p class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              £{{ currentSubscription.plan.billing_period === 'annual' ? currentSubscription.plan.price_annual : currentSubscription.plan.price_monthly }}
              <span class="text-lg font-normal text-gray-600 dark:text-gray-400">
                / {{ currentSubscription.plan.billing_period }}
              </span>
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Next billing date: {{ currentSubscription.current_period_end }}
            </p>
          </div>

          <div>
            <h4 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Current Features
            </h4>
            <ul class="space-y-2">
              <li
                v-for="(feature, index) in currentSubscription.plan.features"
                :key="index"
                class="flex items-start text-sm text-gray-700 dark:text-gray-300"
              >
                <svg class="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                {{ feature }}
              </li>
            </ul>
          </div>
        </div>
      </Card>

      <!-- Billing Interval Toggle -->
      <div class="flex justify-center">
        <div class="inline-flex items-center p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <button
            @click="billingInterval = 'monthly'"
            :class="[
              'px-6 py-2 text-sm font-medium rounded-md transition-all',
              billingInterval === 'monthly'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
            ]"
          >
            Monthly
          </button>
          <button
            @click="billingInterval = 'annual'"
            :class="[
              'px-6 py-2 text-sm font-medium rounded-md transition-all',
              billingInterval === 'annual'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
            ]"
          >
            Annual
            <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      <!-- Available Plans -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card
          v-for="plan in filteredPlans"
          :key="plan.id"
          :class="[
            'relative transition-all',
            plan.id === currentSubscription.plan.id
              ? 'ring-2 ring-blue-500 opacity-50'
              : selectedPlan?.id === plan.id
              ? 'ring-2 ring-blue-500'
              : 'hover:shadow-lg',
          ]"
        >
          <!-- Current Plan Badge -->
          <div
            v-if="plan.id === currentSubscription.plan.id"
            class="absolute top-4 right-4"
          >
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              Current Plan
            </span>
          </div>

          <!-- Popular Badge -->
          <div
            v-else-if="plan.is_popular"
            class="absolute top-4 right-4"
          >
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              Popular
            </span>
          </div>

          <!-- Plan Header -->
          <div class="mb-6">
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {{ plan.name }}
            </h3>
            <div class="flex items-baseline mb-2">
              <span class="text-4xl font-bold text-gray-900 dark:text-white">
                £{{ billingInterval === 'annual' ? plan.price_annual : plan.price_monthly }}
              </span>
              <span class="ml-2 text-gray-600 dark:text-gray-400">
                / {{ billingInterval }}
              </span>
            </div>

            <!-- Price Difference -->
            <div v-if="plan.id !== currentSubscription.plan.id" class="text-sm">
              <span
                v-if="getPriceDifference(plan) > 0"
                class="text-orange-600 dark:text-orange-400 font-medium"
              >
                +£{{ getPriceDifference(plan).toFixed(2) }} / {{ plan.interval }}
              </span>
              <span
                v-else-if="getPriceDifference(plan) < 0"
                class="text-green-600 dark:text-green-400 font-medium"
              >
                £{{ Math.abs(getPriceDifference(plan)).toFixed(2) }} / {{ plan.interval }}
              </span>
            </div>
          </div>

          <!-- Plan Description -->
          <p class="text-gray-600 dark:text-gray-400 text-sm mb-6">
            {{ plan.description }}
          </p>

          <!-- Features -->
          <ul class="space-y-3 mb-6">
            <li
              v-for="(feature, index) in plan.features"
              :key="index"
              class="flex items-start text-sm"
            >
              <svg
                class="w-5 h-5 flex-shrink-0 mr-2"
                :class="isFeatureIncluded(feature) ? 'text-green-500' : 'text-blue-500'"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <span
                :class="[
                  isFeatureIncluded(feature)
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-900 dark:text-white font-medium',
                ]"
              >
                {{ feature }}
              </span>
            </li>
          </ul>

          <!-- Select Button -->
          <Button
            v-if="plan.id !== currentSubscription.plan.id"
            @click="selectPlan(plan)"
            :variant="selectedPlan?.id === plan.id ? 'primary' : 'secondary'"
            class="w-full"
          >
            {{ selectedPlan?.id === plan.id ? 'Selected' : 'Select Plan' }}
          </Button>
          <Button
            v-else
            disabled
            class="w-full"
          >
            Current Plan
          </Button>
        </Card>
      </div>

      <!-- Proration Preview (Upgrades Only) -->
      <Card v-if="selectedPlan && prorationPreview && isUpgrade" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white mb-4">
          {{ prorationPreview.is_free_upgrade ? 'Pricing Preview' : 'Proration Preview' }}
        </h3>

        <div class="space-y-3">
          <!-- Free Upgrade: Simple pricing -->
          <template v-if="prorationPreview.is_free_upgrade">
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-700 dark:text-gray-300">
                {{ selectedPlan.name }} Plan (Full {{ billingInterval }})
              </span>
              <span class="font-medium text-gray-900 dark:text-white">
                £{{ prorationPreview.charge.toFixed(2) }}
              </span>
            </div>

            <!-- Divider -->
            <div class="border-t border-blue-200 dark:border-blue-700 my-2"></div>

            <!-- Net Amount Due -->
            <div class="flex items-center justify-between">
              <span class="font-semibold text-gray-900 dark:text-white">
                Amount due today
              </span>
              <span class="text-2xl font-bold text-gray-900 dark:text-white">
                £{{ prorationPreview.net.toFixed(2) }}
              </span>
            </div>
          </template>

          <!-- Paid Upgrade: Show proration breakdown -->
          <template v-else>
            <!-- Credit for Unused Time -->
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-700 dark:text-gray-300">
                Credit for unused time on {{ currentSubscription.plan.name }}
              </span>
              <span class="font-medium text-green-600 dark:text-green-400">
                -£{{ prorationPreview.credit.toFixed(2) }}
              </span>
            </div>

            <!-- Charge for New Plan -->
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-700 dark:text-gray-300">
                Charge for {{ selectedPlan.name }} (prorated)
              </span>
              <span class="font-medium text-gray-900 dark:text-white">
                £{{ prorationPreview.charge.toFixed(2) }}
              </span>
            </div>

            <!-- Divider -->
            <div class="border-t border-blue-200 dark:border-blue-700 my-2"></div>

            <!-- Net Amount Due -->
            <div class="flex items-center justify-between">
              <span class="font-semibold text-gray-900 dark:text-white">
                Amount due today
              </span>
              <span class="text-2xl font-bold text-gray-900 dark:text-white">
                £{{ prorationPreview.net.toFixed(2) }}
              </span>
            </div>
          </template>

          <!-- Next Billing -->
          <div class="text-sm text-gray-600 dark:text-gray-400 pt-2">
            Your next billing date will be {{ prorationPreview.next_billing_date }}.
            You'll be charged £{{ billingInterval === 'annual' ? selectedPlan.price_annual : selectedPlan.price_monthly }} {{ billingInterval === 'monthly' ? 'monthly' : 'annually' }} starting from that date.
          </div>
        </div>

        <!-- Timing Information -->
        <div class="mt-6">
          <Alert variant="info">
            <template #title>Immediate Upgrade</template>
            <p v-if="prorationPreview.is_free_upgrade">
              Your upgrade will be processed immediately. You'll start a new {{ billingInterval }} billing period from today.
            </p>
            <p v-else>
              Upgrades are applied immediately. You'll be charged the prorated amount today, and your billing cycle remains unchanged.
            </p>
          </Alert>
        </div>
      </Card>

      <!-- Downgrade Information -->
      <Card v-if="selectedPlan && !isUpgrade" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white mb-4">
          Downgrade Information
        </h3>

        <!-- Storage Validation Error -->
        <Alert v-if="storageValidationError" variant="danger" class="mb-4">
          <template #title>
            <div class="flex items-center">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Storage Limit Exceeded
            </div>
          </template>
          <p class="mb-3">{{ storageValidationError.message }}</p>

          <div v-if="storageValidationError.details" class="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <div class="space-y-2 text-sm">
              <div class="flex items-center justify-between">
                <span class="font-medium">Current Usage:</span>
                <span class="font-bold">{{ storageValidationError.details.current_usage }} GB</span>
              </div>
              <div v-if="storageValidationError.details.document_upload_disabled" class="flex items-center justify-between">
                <span class="font-medium">New Plan Limit:</span>
                <span class="font-bold">0 GB (No document upload)</span>
              </div>
              <template v-else>
                <div class="flex items-center justify-between">
                  <span class="font-medium">New Plan Storage:</span>
                  <span>{{ storageValidationError.details.new_plan_storage }} GB</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="font-medium">Your Add-on Storage:</span>
                  <span>{{ storageValidationError.details.addon_storage }} GB</span>
                </div>
                <div class="flex items-center justify-between pt-2 border-t border-red-300 dark:border-red-700">
                  <span class="font-medium">Total Available:</span>
                  <span class="font-bold">{{ storageValidationError.details.total_available }} GB</span>
                </div>
              </template>
              <div class="flex items-center justify-between pt-2 border-t border-red-300 dark:border-red-700">
                <span class="font-medium text-red-700 dark:text-red-300">You need to delete:</span>
                <span class="font-bold text-red-700 dark:text-red-300">{{ storageValidationError.details.excess_gb }} GB</span>
              </div>
            </div>
          </div>
        </Alert>

        <Alert variant="warning">
          <template #title>Scheduled Downgrade</template>
          <p class="mb-4">
            Downgrades take effect at the end of your current billing period ({{ currentSubscription.current_period_end }}).
            You'll continue to enjoy your current <strong>{{ currentSubscription.plan.name }}</strong> plan features until then.
          </p>
          <div class="space-y-2 text-sm">
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">Current Plan:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ currentSubscription.plan.name }} - £{{ currentSubscription.plan.price }}/{{ currentSubscription.plan.interval }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">New Plan:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ selectedPlan.name }} - £{{ selectedPlan.price }}/{{ selectedPlan.interval }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-700 dark:text-gray-300">Effective Date:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ currentSubscription.current_period_end }}</span>
            </div>
            <div class="flex items-center justify-between pt-2 border-t border-amber-200 dark:border-amber-700">
              <span class="text-gray-700 dark:text-gray-300">Amount Due Today:</span>
              <span class="font-bold text-gray-900 dark:text-white">£0.00</span>
            </div>
          </div>
        </Alert>
      </Card>

      <!-- Confirmation -->
      <Card v-if="selectedPlan">
        <Alert variant="info" class="mb-6">
          <template #title>Review Your Changes</template>
          You're about to {{ isUpgrade ? 'upgrade' : 'downgrade' }} from
          <strong>{{ currentSubscription.plan.name }}</strong> to
          <strong>{{ selectedPlan.name }}</strong>.
          <span v-if="isUpgrade">
            You'll be charged £{{ prorationPreview?.net.toFixed(2) }} today, and your plan will be upgraded immediately.
          </span>
          <span v-else>
            The change will take effect at the end of your current billing period ({{ currentSubscription.current_period_end }}).
          </span>
        </Alert>

        <div class="flex justify-end space-x-4">
          <Button
            variant="secondary"
            @click="cancelSelection"
          >
            Cancel
          </Button>
          <Button
            @click="confirmPlanChange"
            :loading="processing"
            :disabled="!isUpgrade && storageValidationError !== null"
          >
            {{ isUpgrade ? 'Confirm & Pay Now' : 'Schedule Downgrade' }}
          </Button>
        </div>
      </Card>
    </div>

    <!-- Success Modal -->
    <Modal :show="showSuccessModal" @close="redirectToBilling">
      <template #title>
        <div class="flex items-center text-green-600 dark:text-green-400">
          <svg class="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Plan Changed Successfully
        </div>
      </template>

      <div class="space-y-4">
        <p class="text-gray-700 dark:text-gray-300">
          Your subscription has been updated to <strong>{{ selectedPlan?.name }}</strong>.
        </p>
        <p v-if="isUpgrade" class="text-sm text-gray-600 dark:text-gray-400">
          Your payment of £{{ prorationPreview?.net.toFixed(2) }} has been processed successfully. Your new plan is active now.
        </p>
        <p v-else class="text-sm text-gray-600 dark:text-gray-400">
          Your plan change has been scheduled and will take effect on {{ currentSubscription.current_period_end }}.
        </p>

        <div class="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button @click="redirectToBilling">
            Go to Billing
          </Button>
        </div>
      </div>
    </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Head, router, Link } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Alert from '@/Components/Common/Alert.vue';
import Modal from '@/Components/Common/Modal.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

// Props
const props = defineProps({
  currentSubscription: {
    type: Object,
    required: true,
  },
  plans: {
    type: Array,
    required: true,
  },
});

// Composables
const { success, error } = useToast();

// Refs
const billingInterval = ref(props.currentSubscription.plan.billing_period);
const selectedPlan = ref(null);
const prorationPreview = ref(null);
const processing = ref(false);
const showSuccessModal = ref(false);
const storageValidationError = ref(null);

// Computed
const filteredPlans = computed(() => {
  // Show all plans regardless of billing interval
  // The billing interval toggle only affects which price is displayed (monthly vs annual)
  return props.plans;
});

const isUpgrade = computed(() => {
  if (!selectedPlan.value) return false;
  const currentPrice = parseFloat(props.currentSubscription.plan.billing_period === 'annual'
    ? props.currentSubscription.plan.price_annual
    : props.currentSubscription.plan.price_monthly);
  const newPrice = parseFloat(billingInterval.value === 'annual'
    ? selectedPlan.value.price_annual
    : selectedPlan.value.price_monthly);
  return newPrice > currentPrice;
});

// Methods
const getPriceDifference = (plan) => {
  const currentPrice = parseFloat(props.currentSubscription.plan.billing_period === 'annual'
    ? props.currentSubscription.plan.price_annual
    : props.currentSubscription.plan.price_monthly);
  const newPrice = parseFloat(billingInterval.value === 'annual'
    ? plan.price_annual
    : plan.price_monthly);
  return newPrice - currentPrice;
};

const isFeatureIncluded = (feature) => {
  return props.currentSubscription.plan.features?.includes(feature);
};

const selectPlan = async (plan) => {
  selectedPlan.value = plan;
  storageValidationError.value = null; // Clear any previous errors
  await calculateProration();
};

const calculateProration = async () => {
  if (!selectedPlan.value) return;

  try {
    // Convert prices to numbers to ensure we can use toFixed() later
    const currentPrice = parseFloat(props.currentSubscription.plan.billing_period === 'annual'
      ? props.currentSubscription.plan.price_annual
      : props.currentSubscription.plan.price_monthly);
    const newPrice = parseFloat(billingInterval.value === 'annual'
      ? selectedPlan.value.price_annual
      : selectedPlan.value.price_monthly);

    // Check if this is a free-to-paid upgrade
    const isFreeUpgrade = currentPrice === 0 || isNaN(currentPrice);

    if (isFreeUpgrade) {
      // FREE to PAID: No proration, charge full amount for new billing period
      const now = new Date();
      const nextBillingDate = new Date(now);

      // Add billing period to current date
      if (billingInterval.value === 'annual') {
        nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
      } else {
        nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
      }

      prorationPreview.value = {
        credit: 0,
        charge: newPrice,
        net: newPrice,
        next_billing_date: nextBillingDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        is_free_upgrade: true,
      };
    } else {
      // PAID to PAID: Calculate proration based on remaining days
      const daysRemaining = getDaysRemaining();
      const daysInPeriod = 30; // Simplified

      const unusedCredit = (currentPrice / daysInPeriod) * daysRemaining;
      const newCharge = (newPrice / daysInPeriod) * daysRemaining;
      const net = Math.max(0, newCharge - unusedCredit);

      const nextBillingDate = new Date(props.currentSubscription.current_period_end);

      prorationPreview.value = {
        credit: unusedCredit,
        charge: newCharge,
        net: net,
        next_billing_date: nextBillingDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        is_free_upgrade: false,
      };
    }
  } catch (err) {
    console.error('Failed to calculate proration:', err);
    error('Failed to calculate proration. Please try again.');
  }
};

const getDaysRemaining = () => {
  const now = new Date();
  const periodEnd = new Date(props.currentSubscription.current_period_end);
  const diff = periodEnd - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const cancelSelection = () => {
  selectedPlan.value = null;
  prorationPreview.value = null;
};

const confirmPlanChange = async () => {
  if (!selectedPlan.value) return;

  processing.value = true;
  storageValidationError.value = null; // Clear previous errors

  try {
    const response = await axios.post(route('api.settings.billing.change-plan'), {
      plan_id: selectedPlan.value.id,
      _company_id: props.currentSubscription.company_id, // Required for company context validation
    });

    if (response.data.success) {
      showSuccessModal.value = true;
    } else {
      error(response.data.message || 'Failed to change plan');
    }
  } catch (err) {
    console.error('Failed to change plan:', err);

    // Check if this is a storage validation error (422 status)
    if (err.response?.status === 422 && err.response?.data?.storage_validation) {
      storageValidationError.value = {
        message: err.response.data.message,
        details: err.response.data.storage_validation,
      };
    }

    const errorMessage = err.response?.data?.message || 'Failed to change plan. Please try again.';
    error(errorMessage);
  } finally {
    processing.value = false;
  }
};

const redirectToBilling = () => {
  router.visit(route('settings.billing.index'));
};

// Watch billing interval changes
watch(billingInterval, () => {
  selectedPlan.value = null;
  prorationPreview.value = null;
});
</script>
